/**
 * 온보딩 후속 게이트 — 사업장 홈(허브) 도착 시 1회성 후속 흐름을 순서대로 튼다.
 *
 * 배경(2026-08-19 실측·사장님 지시): 온보딩 생성이 끝나면 서버가 완료를 자동
 * 마킹하는데, 완료 화면을 거치지 않고 이탈한 사용자는 ① 생성 RA 항목 검토
 * 팝업을 못 봐 **선택 없이 전량 확정**되고 ② 만들어진 서류가 어디 있는지 안내를
 * 못 받았다. 또 ③ 첫 이관은 허브로 보내는 대신 팝업으로 체험시키기로 했다.
 *
 * 순서: RA 항목 검토(미검토자만, 자동 오픈) → 첫 이관 안내 팝업(이관 이력 0건 +
 * 미시청자만) → '지금 옮겨보기'면 그 자리에서 DocImportModal.
 * 열람 기록은 서버 intro_seen(ra_review · first_import) — 기기·재로그인 무관 1회.
 *
 * 기존 사용자(2026-08-20 지시): 이관 배포 전에 가입한 사용자는 온보딩 잡이 없거나
 * 오래됐다. 그들에게는 **첫 이관 팝업만** 1회 띄운다 — 이관 팝업은 온보딩 잡이
 * 아니라 "사이트 보유 + 이관 이력 0건 + 미시청"으로 판단하고, RA 검토는 배포
 * 마이그레이션(093)이 intro_seen.ra_review 를 백필해 뜨지 않는다.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FiCheckCircle, FiClipboard } from 'react-icons/fi'
import { onboardingApi, type IntroPageKey } from './onboardingApi'
import { importsApi } from '../../lib/importsApi'
import { sitesApi } from '../../lib/sitesApi'
import { isDraftSite } from '../../components/common/SiteRegistrationGate'
import { useAuthStore } from '../../store/authStore'
import { toast } from '../../hooks/useToast'
import { RaReviewModal } from './TaskSetupSteps'
import FirstImportPopup from './FirstImportPopup'
import DocImportModal from '../../pages/sites/DocImportModal'

/** AppShell 전역 마운트(2026-08-19 실측 수정): 이탈자의 재방문 랜딩은 사업장 홈이
 * 아니라 /dashboard 홈이라, 사업장 홈에만 두면 후속 흐름을 영영 못 만난다. */
export default function OnboardingFollowupGate() {
  const user = useAuthStore(s => s.user)
  const setUser = useAuthStore(s => s.setUser)
  const seen = (user?.intro_seen ?? {}) as Record<string, unknown>

  const curQ = useQuery({
    queryKey: ['onb-setup-current'],
    queryFn: () => onboardingApi.setupCurrent(),
    staleTime: 300_000,
    // 두 후속이 모두 끝난 사용자는 호출 자체를 생략(불필요한 요청 방지).
    enabled: !seen['ra_review'] || !seen['first_import'],
  })
  const quotaQ = useQuery({
    queryKey: ['imports', 'quota'],
    queryFn: () => importsApi.quota(),
    staleTime: 60_000,
    enabled: !seen['first_import'],
  })
  // 온보딩 잡이 없는 기존 사용자용 — 이관 대상 사이트를 첫 사업장으로 잡는다.
  const sitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list(),
    staleTime: 300_000,
    enabled: !seen['first_import'],
  })

  const [raOpen, setRaOpen] = useState(false)
  const [raDone, setRaDone] = useState(false)
  const [introOpen, setIntroOpen] = useState(false)
  const [docImportOpen, setDocImportOpen] = useState(false)

  const job = curQ.data
  const jobDone = !!job?.job_id && job.status === 'done'
  // 온보딩을 거친 사용자는 그 잡의 사이트, 기존 사용자는 **정보가 채워진** 첫 사업장.
  // 초안(업종·인원 미입력)뿐이면 SiteRegistrationGate 의 정보 입력 모달이 우선이라
  // 이관 팝업을 보류한다 — 마킹하지 않으므로 정보 입력 후 다음 방문에 뜬다(겹침 실측).
  const readySiteId = (sitesQ.data || []).find(s => !isDraftSite(s))?.id || ''
  const siteId = job?.site_id || readySiteId
  const needRaReview = jobDone && !!job?.site_id && !!job?.ra_ref_id && !seen['ra_review'] && !raDone
  // 첫 이관 팝업은 온보딩 잡과 무관 — 사이트 보유 + 이관 이력 0건 + 미시청이면 띄운다.
  const canFirstImport = !!siteId && !seen['first_import']
    && quotaQ.data != null && (quotaQ.data.used_files ?? 0) === 0

  // RA 검토 자동 오픈 → 닫힌 뒤 첫 이관 팝업 순서로.
  useEffect(() => { if (needRaReview) setRaOpen(true) }, [needRaReview])
  useEffect(() => {
    if (!needRaReview && !raOpen && !docImportOpen && canFirstImport) setIntroOpen(true)
  }, [needRaReview, raOpen, docImportOpen, canFirstImport])

  function mark(key: Extract<IntroPageKey, 'ra_review' | 'first_import'>,
                choice?: 'started' | 'later') {
    onboardingApi.markIntroSeen(key, 0, true, choice)
      .then(r => { if (user) setUser({ ...user, intro_seen: r.intro_seen }) })
      .catch(() => { /* 기록 실패는 다음 방문 재노출로 자연 복구 */ })
  }

  return (
    <>
      {/* 재방문 안내 배너 — RA 검토가 남아 있는 동안만. */}
      {needRaReview && (
        <div className="mb-3 flex flex-wrap items-center gap-2.5 rounded-xl border border-amber-400/40 bg-amber-500/[0.07] px-4 py-3">
          <FiCheckCircle className="shrink-0 text-amber-500" size={18} />
          <p className="min-w-0 flex-1 text-[12.5px] text-ink">
            <b>지난번 만든 서류 {job?.created_count ?? 4}건이 준비됐어요.</b>{' '}
            <span className="text-ink-muted">위험성평가 항목을 확인·선택하면 평가표가 확정됩니다 — 서류는{' '}
              <Link to={`/dashboard/site/${siteId}`} className="font-semibold text-brand-primary hover:underline">사업장 홈의 작업 보드</Link>에 있어요.</span>
          </p>
          <button type="button" onClick={() => setRaOpen(true)}
            className="shrink-0 rounded-lg bg-amber-500/15 px-3 py-1.5 text-[12px] font-bold text-amber-700 hover:bg-amber-500/25 dark:text-amber-300">
            <FiClipboard className="-mt-0.5 mr-1 inline" size={13} /> 항목 확인하기
          </button>
        </div>
      )}

      {raOpen && job?.ra_ref_id && (
        <RaReviewModal siteId={siteId} raId={job.ra_ref_id}
          onClose={() => { setRaOpen(false); setRaDone(true); mark('ra_review') }} />
      )}

      <FirstImportPopup open={introOpen}
        onStart={() => { setIntroOpen(false); mark('first_import', 'started'); setDocImportOpen(true) }}
        onLater={() => {
          setIntroOpen(false); mark('first_import', 'later')
          toast.info('언제든 사업장 홈의 "문서 업로드·분석"에서 옮길 수 있어요.')
        }} />

      <DocImportModal siteId={siteId} open={docImportOpen}
        onClose={() => setDocImportOpen(false)} />
    </>
  )
}
